import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import { Button } from "@/components/ui/button";
// import { Spinner } from "@/components/ui/spinner";
import { useNavigate, useParams } from "react-router-dom";
import { ThrowError } from "@/api/callApi";
import moment from "moment";
import {
  ArrowLeft,
  Calendar,
  FileText,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { CompanyDocumentServices } from "@/services/CompanyDocument/CompanyDocumentServices";
import type { ICompanyDocument } from "@/interface/companyDocument.interface";
import DOMPurify from "dompurify";
import FullPageLoader from "@/components/common/FullPageLoader";

const ViewCompanyDocument: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [document, setDocument] = useState<ICompanyDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await CompanyDocumentServices.getCompanyDocumentById(id);
      if (response?.status && response.data) {
        setDocument(response.data);
      } else {
        setError("Document details not found");
      }
    } catch (err) {
      ThrowError(err);
      setError("Failed to load document details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (isLoading) {
    return (
      <Layout>
        {/* <div className="flex items-center justify-center min-h-[400px]"> */}
        <FullPageLoader />
        {/* <Spinner className="h-8 w-8 text-primary animate-spin" /> */}
        {/* </div> */}
      </Layout >
    );
  }

  if (error || !document) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-700">
            {error || "Document not found"}
          </h3>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </Layout>
    );
  }

  const statusClass = document.isActive
    ? "bg-green-50 text-green-700 border-green-200"
    : "bg-gray-50 text-gray-700 border-gray-200";

  const sanitizedContent = DOMPurify.sanitize(document.content || "");

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <MainHeading
            title="Document Details"
            isBackButtonVisible
            onClick={() => navigate(-1)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <FileText className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Document Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <InfoItem label="Title" value={document.title} />
                <InfoItem label="Document Key" value={document.documentKey} />
                <InfoItem label="Type" value={document.type} />
                <InfoItem label="Version" value={document.version} />
                <InfoItem
                  label="Effective Date"
                  value={
                    document.effectiveDate
                      ? moment(document.effectiveDate).format("DD-MM-YYYY")
                      : "—"
                  }
                />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${statusClass}`}
                  >
                    {document.isActive ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" /> Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" /> Inactive
                      </>
                    )}
                  </span>
                </div>
              </div>
            </Card>

            {document.description && (
              <Card>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <Info className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Description
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {document.description}
                </p>
              </Card>
            )}

            {document.content && (
              <Card>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Content
                  </h3>
                </div>
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Timeline
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Created At"
                  value={
                    document.createdAt
                      ? moment(document.createdAt).format("DD-MM-YYYY HH:mm")
                      : "—"
                  }
                />
                {document.updatedAt && document.updatedAt !== document.createdAt && (
                  <InfoItem
                    label="Last Updated"
                    value={moment(document.updatedAt).format("DD-MM-YYYY HH:mm")}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-sm font-semibold text-foreground break-words">
      {value || "—"}
    </p>
  </div>
);

export default ViewCompanyDocument;